<template>
  <data-table
    :key="tableKey"
    :permissions="permissions"
    :crudPerms="crudPerms"
    :title="title"
    :headers="headersLocal"
    :adjunctHeaders="adjunctHeaders"
    :itemTemplate="templateLocal"
    :adjunctItemTemplate="adjunctItemTemplate"
    :items="!items ? itemsLocal : items"
    :totalItems="totalItems"
    :disabled="disabled || loading"
    :loading="loading"
    :allowDownload="allowDownload"
    :allowRefresh="allowRefresh"
    :allowAdd="allowAdd"
    :lastRowAdd="lastRowAdd"
    :allowEdit="allowEdit"
    :allowDelete="allowDelete"
    :allowSearch="allowSearch"
    :allowSort="allowSort"
    :allowDblClickEdit="allowEdit && allowDblClickEdit"
    :customButtons="customButtons"
    :revert="revert"
    @add-item="insertItem($event, true)"
    @update-item="updateItem($event, true)"
    @delete-item="deleteItem($event, true)"
    @refresh="selectItems()"
    @refresh-item="selectItem($event)"
    @download="download($event)"
    @copy="copy($event)"
    @custom-toolbar-button="$emit('custom-toolbar-button', $event)"
    @custom-item-action="$emit('custom-item-action', $event)"
    @reverted="revert = false"
    @query-updated="saveQuery($event)"
    class="px-3"
  ></data-table>
</template>
<script>
import DataTable from './children/DataTable.vue';

const fetchV2 = require('../../assets/js/fetchV2');

export default {
  name: 'CrudElectron',

  components: {
    DataTable,
  },

  props: {
    permissions: {
      type: Number,
      required: true,
    },

    crudPerms: {
      type: Number,
      required: true,
    },

    title: {
      type: String,
      required: false,
    },

    crudRoute: {
      type: String,
      required: true,
    },

    joinItemsEndpoint: {
      type: Array,
      required: false,
      default: () => [],
    },

    headers: {
      type: Array,
      required: true,
    },

    addHeaders: {
      type: Array,
      required: false,
      default: () => [],
    },

    addHeadersEndpoint: {
      type: Array,
      required: false,
      default: () => [],
    },

    adjunctHeaders: {
      type: Array,
      required: false,
      default: () => [],
    },

    itemTemplate: {
      type: Object,
      required: true,
    },

    addTemplateItems: {
      type: Object,
      required: false,
      default: () => ({}),
    },

    addTemplateItemsEndpoint: {
      type: Array,
      required: false,
      default: () => [],
    },

    adjunctItemTemplate: {
      type: Object,
      required: false,
      default: () => {},
    },

    items: {
      type: Array,
      required: false,
      default: null,
    },

    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowDownload: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowRefresh: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowAdd: {
      type: Boolean,
      required: false,
      default: true,
    },

    lastRowAdd: {
      type: Boolean,
      required: false,
      default: false,
    },

    allowEdit: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowDelete: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowSearch: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowSort: {
      type: Boolean,
      required: false,
      default: true,
    },

    allowDblClickEdit: {
      type: Boolean,
      required: false,
      default: true,
    },

    customButtons: {
      type: Array,
      required: false,
      default: () => [],
    },
  },

  data: () => ({
    tableKey: 0,
    headersLocal: [],
    templateLocal: {},
    itemsLocal: [],
    totalItems: 0,
    loading: false,
    revert: false,
    query: {},
  }),

  watch: {
    itemTemplate: {
      handler() {
        this.getTemplateItems().then(() => {
          if (!this.items) {
            this.selectItems();
          }
        })
      },
      deep: true,
    },
  },

  computed: {
    primaryKeys() {
      return this.headers.filter(f => f.primaryKey).map(f => f.value);
    },
  },

  methods: {
    saveQuery(q) {
      console.log('Saving query to route:', q);
      this.query = q;
    },

    generateQueryString() {
      return this.query.map(item => `${item.field}=${item.value}`).join('&');
    },

    getItems() {
      return this.items ? this.items : this.itemsLocal;
    },

    setItems(items) {
      if (!this.items) {
        this.itemsLocal = items;
      }
      else {
        this.$emit('update-items', items);
      }
      this.tableKey++;
    },

    notEqualKeys(keys, a, b) {
      for (const key of keys) {
        if (a[key] !== b[key]) {
          return true;
        }
      }
      return false;
    },

    getHeaderItems() {
      return new Promise(async (resolve, reject) => {
        this.headersLocal = JSON.parse(JSON.stringify(this.headers));

        if (this.addHeaders && Array.isArray(this.addHeaders)) {
          this.headersLocal = this.headersLocal.concat(this.addHeaders);
        }

        if (this.addHeadersEndpoint && Array.isArray(this.addHeadersEndpoint) && this.addHeadersEndpoint.length > 0) {
          for (const endpoint of this.addHeadersEndpoint) {
            try {
              const additionalHeaders = await fetchV2(`GET ${endpoint}`, {}, { 'pragma': 'no-cache', 'cache-control': 'no-cache' });
              this.headersLocal = this.headersLocal.concat(additionalHeaders);
            }
            catch (err) {
              this.$emit('error', err && err.length ? err : 'Error fetching headers.');
              return reject();
            }
          }
        }

        return resolve();
      })
    },

    getTemplateItems() {
      return new Promise(async (resolve, reject) => {
        this.templateLocal = JSON.parse(JSON.stringify(this.itemTemplate));

        if (this.addTemplateItems && typeof this.addTemplateItems === 'object' && !Array.isArray(this.addTemplateItems)) {
          this.templateLocal = { ...this.templateLocal, ...this.addTemplateItems };
        }

        if (this.addTemplateItemsEndpoint && Array.isArray(this.addTemplateItemsEndpoint) && this.addTemplateItemsEndpoint.length > 0) {
          for (const endpoint of this.addTemplateItemsEndpoint) {
            try {
              const additionalItemTemplates = await fetchV2(`GET ${endpoint}`, {}, { 'pragma': 'no-cache', 'cache-control': 'no-cache' });
              this.templateLocal = { ...this.templateLocal, ...additionalItemTemplates };
            }
            catch (err) {
              this.$emit('error', err && err.length ? err : 'Error fetching items.');
              return reject();
            }
          }
        }

        for (const key of Object.keys(this.templateLocal)) {
          if (this.templateLocal[key].itemsEndpoint) {
            try {
              this.templateLocal[key].items = await fetchV2(`GET ${this.templateLocal[key].itemsEndpoint}`, {}, { 'pragma': 'no-cache', 'cache-control': 'no-cache' });
              this.templateLocal[key].items.sort((a, b) => a.text.localeCompare(b.text))
            }
            catch (err) {
              this.$emit('error', err && err.length ? err : 'Error fetching items.');
              return reject();
            }
          }
        }

        return resolve();
      })
    },

    getURLParameters() {
      const params = {};
      const queryString = window.location.search.substring(1);
      const vars = queryString.split('&');
      for (let i = 0; i < vars.length; i++) {
        const pair = vars[i].split('=');
        params[decodeURIComponent(pair[0])] = decodeURIComponent(pair[1]);
      }
      return params;
    },

    selectItems(complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        // Add all query parameters to avoid cached results
        const currentParams = this.getURLParameters();
        const query = `?t=${new Date().getTime()}` + Object.keys(currentParams).map(key => `&${key}=${currentParams[key]}`).join('');

        fetchV2(`GET ${this.crudRoute}${query}`, {}, { 'pragma': 'no-cache', 'cache-control': 'no-cache' }).then(async tableItems => {

          // Some APIs return { total: X, rows: [...] } instead of just [...]
          if (tableItems && tableItems.total) {
            this.totalItems = tableItems.total;
            tableItems = tableItems.rows;
          }
          else {
            this.totalItems = tableItems.length;
          }

          if (this.joinItemsEndpoint && Array.isArray(this.joinItemsEndpoint) && this.joinItemsEndpoint.length > 0) {
            for (const endpoint of this.joinItemsEndpoint) {
              try {
                const joinItems = await fetchV2(`GET ${endpoint.endpoint}`, {}, { 'pragma': 'no-cache', 'cache-control': 'no-cache' });

                console.log('Joining items from', endpoint.endpoint, 'with main keys', endpoint.mainKeys, 'and join keys', endpoint.joinKeys);

                // Iterate through each new joined item
                for (const jItem of joinItems) {
                  // Find the corresponding item in the table to attach to
                  for (const tItem of tableItems) {
                    // Check if the main keys match the join keys
                    let match = true;
                    for (const i in endpoint.mainKeys) {
                      if (tItem[endpoint.mainKeys[i]] !== jItem[endpoint.joinKeys[i]]) {
                        match = false;
                        break;
                      }
                    }

                    // Attach the columns to the table item
                    if (match) {
                      for (const key of Object.keys(jItem)) {
                        if (!endpoint.joinKeys.includes(key)) {
                          tItem[key] = jItem[key];
                        }
                      }
                    }
                  }
                }
              }
              catch (err) {
                if (complete) {
                  this.loading = false;
                  this.$emit('error', err && err.length ? err : 'Error fetching items.');
                }
                return reject();
              }
            }

            this.setItems(tableItems);
            if (complete) {
              this.$emit('success-banner', 'Items fetched successfully.');
              this.loading = false;
            }
            return resolve();
          }
          else {
            this.setItems(tableItems);
            if (complete) {
              this.$emit('success-banner', 'Items fetched successfully.');
              this.loading = false;
            }
            return resolve();
          }
        }).catch(err => {
          if (complete) {
            this.loading = false;
            this.$emit('error', err && err.length ? err : 'Error fetching items.');
          }
          return reject();
        })
      })
    },

    selectItem(item, complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        const newItem = {};
        for (const key of this.primaryKeys) {
          newItem[key] = item[key];
        }
        const query = Object.keys(newItem).map(key => `${key}=${newItem[key]}`).join('&');

        fetchV2(`GET ${this.crudRoute}?${query}`, {}, { 'pragma': 'no-cache', 'cache-control': 'no-cache' }).then(data => {
          const idx = this.getItems().findIndex(f => this.primaryKeys.every(key => f[key] === data[0][key]));
          if (idx !== -1) {
            // Replace the item immutably so Vue reactivity and child components update
            const updated = [...this.getItems()];
            updated[idx] = data[0];
            this.setItems(updated);
          }
          else {
            this.setItems([...this.getItems(), data[0]]);
          }

          if (complete) {
            this.$emit('success-banner', 'Item fetched successfully.');
            this.loading = false;
          }
          return resolve();
        }).catch(err => {
          if (complete) {
            this.loading = false;
            this.$emit('error', err && err.length ? err : 'Error fetching item.');
          }
          return reject();
        })
      })
    },

    insertItem(item, complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        fetchV2(`POST ${this.crudRoute}`, item).then(data => {
          if (complete) {
            this.loading = false;
            this.$emit('success-banner', 'Item added successfully.');
          }
          return resolve();
        }).catch(err => {
          // On error, refresh entire table to restore consistency
          const emitErr = typeof err === 'string' && err.length ? err : 'Error adding item.';
          if (complete) {
            this.$emit('error', emitErr);
          }
          this.selectItems().then(() => {
            return reject(err);
          }).catch(() => {
            return reject(err);
          });
        })
      })
    },

    updateItem(items, complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        const changedKeys = [];
        let pkChange = false;
        // Check if primary key has been modified and get changed fields
        for (const key of Object.keys(items.old)) {
          if (items.old[key] !== items.new[key]) {
            if (this.primaryKeys.includes(key)) {
              pkChange = true;
            }
            changedKeys.push(key);
          }
        }

        if (changedKeys.length === 0) {
          this.loading = false;
          return resolve();
        }

        // If primary key was modified, delete the old item and add the new item
        else if (pkChange) {
          // Delete old and insert new locally (both called with complete=false so they won't refetch)
          this.deleteItem(items.old, false).then(() => {
            this.insertItem(items.new, false).then(() => {
              // Both operations already updated the local items.
              if (complete) {
                this.loading = false;
                this.$emit('success-banner', 'Item updated successfully.');
              }
              return resolve();
            }).catch(err => {
              // On any failure, refresh the entire table to restore consistency
              const emitErr = err && err.length ? err : 'Error updating item. The entry has been removed from the database.';
              if (complete) {
                this.$emit('error', emitErr);
              }
              this.selectItems().then(() => {
                return reject(err);
              }).catch(() => {
                return reject(err);
              });
            })
          }).catch(err => {
            const emitErr = err && err.length ? err : 'Error updating item.';
            if (complete) {
              this.$emit('error', emitErr);
            }
            this.selectItems().then(() => {
              return reject(err);
            }).catch(() => {
              return reject(err);
            });
          })
        }
        // If primary key was not modified, update the item in-place
        else {
          // Normal in-place update (no primary key change)
          fetchV2(`PUT ${this.crudRoute}`, items.new).then(data => {
            try {
              if (complete) {
                this.loading = false;
                this.$emit('success-banner', 'Item updated successfully.');
              }
              return resolve();
            }
            catch (err) {
              // If something unexpected happens when applying locally, refresh table
              if (complete) {
                this.$emit('error', err && err.length ? err : 'Error updating item.');
              }
              this.selectItems().then(() => {
                return reject(err);
              }).catch(() => {
                return reject(err);
              });
            }
          }).catch(err => {
            // On PUT error, refresh entire table to restore consistency
            const emitErr = err && err.length ? err : 'Error updating items.';
            if (complete) {
              this.$emit('error', emitErr);
            }
            this.selectItems().then(() => {
              return reject(err);
            }).catch(() => {
              return reject(err);
            });
          })
        }
      })
    },

    deleteItem(item, complete = true) {
      this.loading = true;

      return new Promise((resolve, reject) => {
        fetchV2(`DELETE ${this.crudRoute}`, item).then(data => {
          if (complete) {
            this.loading = false;
            this.$emit('success-banner', 'Item deleted successfully.');
          }
          return resolve();
        }).catch(err => {
          // On error, refresh entire table to restore consistency
          const emitErr = err && err.length ? err : 'Error deleting items.';
          if (complete) {
            this.$emit('error', emitErr);
          }
          this.selectItems().then(() => {
            return reject(err);
          }).catch(() => {
            return reject(err);
          });
        })
      })
    },
  },

  destroyed() {

  },

  created() {

  },

  mounted() {
    this.loading = true;
    this.getHeaderItems().then(() => {
      this.getTemplateItems().then(() => {
        this.loading = false;
        if (!this.items) {
          this.selectItems();
        }
      }).catch(err => {
        this.loading = false;
        this.$emit('error', err && err.length ? err : 'Error fetching items.');
      })
    }).catch(err => {
      this.loading = false;
      this.$emit('error', err && err.length ? err : 'Error fetching headers.');
    })
  }
}
</script>
